import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import { deleteDailyQuote, listDailyQuotes, uploadDailyQuote } from '../../api/quotes';
import { API_BASE_URL } from '../../config';

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const todayKey = DAYS[(new Date().getDay() + 6) % 7].key;

export default function QuoteOfDaySection({ token }) {
  const [quotesByDay, setQuotesByDay] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState({});
  const [busyDay, setBusyDay] = useState(null);
  const [dayError, setDayError] = useState({});

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await listDailyQuotes(token);
      const byDay = {};
      data.forEach((q) => {
        byDay[q.day_of_week] = q;
      });
      setQuotesByDay(byDay);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (day, file) => {
    setPendingFiles((prev) => ({ ...prev, [day]: file }));
  };

  const handleUpload = async (day) => {
    const file = pendingFiles[day];
    if (!file) return;
    setBusyDay(day);
    setDayError((prev) => ({ ...prev, [day]: '' }));
    try {
      const formData = new FormData();
      formData.append('image', file);
      await uploadDailyQuote(token, day, formData);
      setPendingFiles((prev) => ({ ...prev, [day]: null }));
      await load();
    } catch (err) {
      setDayError((prev) => ({ ...prev, [day]: err.message }));
    } finally {
      setBusyDay(null);
    }
  };

  const handleRemove = async (day) => {
    if (!window.confirm(`Remove the ${day} quote image?`)) return;
    setBusyDay(day);
    setDayError((prev) => ({ ...prev, [day]: '' }));
    try {
      await deleteDailyQuote(token, day);
      await load();
    } catch (err) {
      setDayError((prev) => ({ ...prev, [day]: err.message }));
    } finally {
      setBusyDay(null);
    }
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Quote of the Day</h2>
      <p className="text-sm text-gray-500 mb-6">
        Upload one image per day of the week. The app automatically shows each day's image on
        that day and switches on its own when the day changes — upload a fresh set whenever you
        want to update the week, otherwise the same 7 images keep repeating week after week.
      </p>

      <ErrorBanner message={error} onRetry={load} />

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DAYS.map(({ key, label }) => {
            const quote = quotesByDay[key];
            const isBusy = busyDay === key;
            const isToday = key === todayKey;
            return (
              <div
                key={key}
                className={`bg-white border rounded-lg p-4 flex flex-col gap-3 ${
                  isToday ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900">{label}</h3>
                  {isToday && (
                    <span className="text-xs font-medium text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5">
                      Today
                    </span>
                  )}
                </div>

                {quote ? (
                  <img
                    src={`${API_BASE_URL}/daily-quotes/${key}/image`}
                    alt={`${label} quote`}
                    className="w-full h-32 object-cover rounded-md border border-gray-100"
                  />
                ) : (
                  <div className="w-full h-32 rounded-md border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">
                    No image set
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(key, e.target.files?.[0] || null)}
                  className="text-xs"
                />

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleUpload(key)}
                    disabled={isBusy || !pendingFiles[key]}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isBusy ? 'Saving...' : quote ? 'Replace' : 'Upload'}
                  </button>
                  {quote && (
                    <button
                      onClick={() => handleRemove(key)}
                      disabled={isBusy}
                      className="text-xs text-red-600 hover:underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                </div>

                {dayError[key] && <p className="text-xs text-red-600">{dayError[key]}</p>}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
